import { useState } from 'react';
import { Info, Settings, Package } from 'lucide-react';
import { useToast } from '../../../components/ui/Toast';
import { SearchInput } from '../../../components/ui/SearchInput';
import { CopyButton } from '../../../components/ui/CopyButton';
import { ColorSwatch } from '../../../components/ui/ColorSwatch';
import { FileUpload } from '../../../components/ui/FileUpload';
import { Accordion } from '../../../components/ui/Accordion';
import { Divider } from '../../../components/ui/Divider';
import { Subsection } from '../components/Subsection';

// ============================================================================
// Sekcja: Specjalne
// ============================================================================

export function SpecializedSection() {
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedEcho, setDebouncedEcho] = useState('');

  return (
    <div className="space-y-10">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        Komponenty wyspecjalizowane: <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<SearchInput>'}</code>,{' '}
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<CopyButton>'}</code>,{' '}
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<ColorSwatch>'}</code>,{' '}
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<FileUpload>'}</code>,{' '}
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<Accordion>'}</code>.
      </p>

      <Subsection n={1} title="SearchInput (z debounce)">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4 space-y-3">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            onDebouncedChange={setDebouncedEcho}
            debounceMs={500}
            placeholder="Wpisz coś (debounce 500ms)…"
          />
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="rounded-lg bg-surface-base/50 border border-border-subtle p-3">
              <div className="text-text-muted mb-1">Bieżąca wartość (natychmiast):</div>
              <div className="font-mono text-text-primary truncate">{searchQuery || '(pusta)'}</div>
            </div>
            <div className="rounded-lg bg-surface-base/50 border border-border-subtle p-3">
              <div className="text-text-muted mb-1">Debounced (po 500ms):</div>
              <div className="font-mono text-accent-primary truncate">{debouncedEcho || '(pusta)'}</div>
            </div>
          </div>
        </div>
      </Subsection>

      <Subsection n={2} title="CopyButton">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4 space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-base/50 border border-border-subtle font-mono text-sm text-text-secondary">
              <span>sk-abc123xyz789</span>
              <CopyButton value="sk-abc123xyz789" variant="icon" size="sm" />
            </div>
            <CopyButton value="https://ecomflow.pl/api/orders/12345" label="Skopiuj link" variant="secondary" size="sm" />
            <CopyButton value="Przykładowy tekst" label="Z toast" />
            <CopyButton value="Cicha kopia" label="Bez toast" showToast={false} />
          </div>
          <p className="text-xs text-text-muted">
            Klik = ikona Copy → Check przez 2s + toast "Skopiowano" (opcjonalne).
          </p>
        </div>
      </Subsection>

      <Subsection n={3} title="ColorSwatch">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4 space-y-5">
          <div>
            <p className="text-xs text-text-muted mb-3">Rozmiary (bez label):</p>
            <div className="flex items-center gap-3">
              <ColorSwatch color="#4a9eff" size="sm" />
              <ColorSwatch color="#4a9eff" size="md" />
              <ColorSwatch color="#4a9eff" size="lg" />
            </div>
          </div>
          <Divider />
          <div>
            <p className="text-xs text-text-muted mb-3">Z label + hex (paleta marki):</p>
            <div className="flex items-start gap-4 flex-wrap">
              <ColorSwatch color="#4a9eff" label="Accent primary" showHex size="lg" />
              <ColorSwatch color="#6b8cce" label="Accent secondary" showHex size="lg" />
              <ColorSwatch color="#10b981" label="Success" showHex size="lg" />
              <ColorSwatch color="#f59e0b" label="Warning" showHex size="lg" />
              <ColorSwatch color="#ef4444" label="Danger" showHex size="lg" />
            </div>
          </div>
          <Divider />
          <div>
            <p className="text-xs text-text-muted mb-3">Z alpha (widoczne checkerboard):</p>
            <div className="flex items-center gap-3">
              <ColorSwatch color="rgba(74, 158, 255, 0.5)" showHex size="md" />
              <ColorSwatch color="rgba(74, 158, 255, 0.2)" showHex size="md" />
              <ColorSwatch color="transparent" showHex size="md" />
            </div>
          </div>
          <Divider />
          <div>
            <p className="text-xs text-text-muted mb-3">Klikalne (z onClick):</p>
            <div className="flex items-center gap-3">
              {['#4a9eff', '#10b981', '#f59e0b', '#ef4444', '#a855f7'].map(c => (
                <ColorSwatch
                  key={c}
                  color={c}
                  size="md"
                  onClick={() => toast.info(`Wybrany kolor: ${c}`)}
                />
              ))}
            </div>
          </div>
        </div>
      </Subsection>

      <Subsection n={4} title="FileUpload">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <FileUpload
            onFiles={(files) => toast.info(`Wybrano ${files.length} plik(ów)`)}
            accept="image/*"
            description="Tylko obrazki (PNG, JPG, WebP)"
          />
          <FileUpload
            onFiles={(files) => toast.info(`Wybrano ${files.length} plik(ów)`)}
            multiple
            maxSize={5 * 1024 * 1024}
            label="Upload wielu plików (max 5 MB każdy)"
            description="Można przeciągnąć kilka naraz"
          />
        </div>
      </Subsection>

      <Subsection n={5} title="Accordion">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-text-muted mb-2">Single open (default):</p>
            <Accordion
              items={[
                {
                  id: 'a',
                  title: 'Jak skonfigurować WooCommerce?',
                  content: (
                    <p>Wejdź w Ustawienia → Integracje → WooCommerce. Wklej klucze API ze sklepu i kliknij Zapisz. Synchronizacja startuje automatycznie.</p>
                  ),
                  defaultOpen: true,
                },
                {
                  id: 'b',
                  title: 'Skąd wziąć klucze API?',
                  content: (
                    <p>Z panelu WooCommerce: Settings → Advanced → REST API → Add key. Uprawnienia: Read/Write.</p>
                  ),
                },
                {
                  id: 'c',
                  title: 'Co jeśli sync nie działa?',
                  content: (
                    <p>Sprawdź czy sklep ma aktywny HTTPS i czy klucze nie wygasły. W razie problemów kontakt na support@ecomflow.pl.</p>
                  ),
                },
              ]}
            />
          </div>
          <div>
            <p className="text-xs text-text-muted mb-2">Allow multiple + z ikonami:</p>
            <Accordion
              allowMultiple
              items={[
                { id: 'x', title: 'Ustawienia ogólne', icon: <Settings />, content: <p>Ogólne preferencje aplikacji.</p>, defaultOpen: true },
                { id: 'y', title: 'Powiadomienia', icon: <Info />, content: <p>Konfiguracja email i push.</p>, defaultOpen: true },
                { id: 'z', title: 'Integracje', icon: <Package />, content: <p>Podłączone systemy zewnętrzne.</p> },
              ]}
            />
          </div>
        </div>
      </Subsection>
    </div>
  );
}
